import * as React from 'react';
import Box from '@mui/material/Box';
import Scrollbar from '../components/scrollbar';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { WarningOutlined } from '@mui/icons-material';
import {
  Card,
  Stack,
  Typography,
  Button,
  Divider,
  Modal,
  Input,
  LinearProgress,
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import api from 'src/services/BaseService';
import { HotTable } from '@handsontable/react';
import 'handsontable/dist/handsontable.full.min.css';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import { useLocalization } from 'src/context/LocalizationContext';

export default function FetchBranches() {
  const { language } = useLocalization();
  const hotRef = React.useRef(null);
  const storedMallID = localStorage.getItem('mallID');

  // Kurumsallar
  const [corpLoading, setCorpLoading] = React.useState(false);
  const [corpTitles, setCorpTitles] = React.useState([]); // dropdown source
  const corpMapRef = React.useRef(new Map()); // title -> taxNo
  const taxNoMapRef = React.useRef(new Map()); // taxNo -> { accountCode, taxOffice }

  // UI state
  const [isSuccessModalOpen, setSuccessModalOpen] = React.useState(false);
  const [successMessage, setSuccessMessage] = React.useState('');

  // Başlıklar
  const columnHeaders = React.useMemo(
    () => [
      language.commons.column.no,
      language.commons.column.mall_id,
      language.commons.column.store_name,
      language.commons.column.commerce_name, // Ticari Ünvan
      language.commons.column.tax_no,
    ],
    [language]
  );

  // Handsontable başlangıç verisi -> state
  const initialRows = React.useMemo(
    () =>
      Array.from({ length: 50 }, (_, index) => [
        index + 1, // No
        storedMallID, // AVM ID
        '', // Mağaza Adı
        '', // Ticari Ünvan (dropdown)
        '', // Vergi No (disabled)
      ]),
    [storedMallID]
  );
  const [hotData, setHotData] = React.useState(initialRows);

  // Kolon ayarları (data YOK!)
  const columnSettings = React.useMemo(
    () => [
      { readOnly: true, width: 60, className: 'htCenter htMiddle' }, // No
      { readOnly: true, width: 180, className: 'htCenter htMiddle' }, // AVM ID
      { strict: false, width: 220, className: 'htCenter htMiddle' }, // Mağaza Adı
      {
        // Ticari Ünvan
        type: 'dropdown',
        source: corpTitles,
        strict: true,
        allowInvalid: false,
        width: 260,
        className: 'htCenter htMiddle',
      },
      { readOnly: true, width: 180, className: 'htCenter htMiddle' }, // Vergi No
    ],
    [corpTitles]
  );

  // Kurumsalları çek
  const fetchCorporations = async () => {
    try {
      setCorpLoading(true);
      const res = await api.post('flow/get-corporations');
      const raw = Array.isArray(res?.data)
        ? res.data
        : res?.data?.data || res?.data?.result || res?.data?.rows || [];

      const map = new Map();
      const taxNoMap = new Map();
      raw.forEach((corp) => {
        const title = String(corp?.title || '').trim();
        const taxNo = String(corp?.billing?.taxNo || '').trim();
        const accountCode = String(
          corp?.companyCorporationalCode ||
            corp?.corporationID ||
            corp?.branchID ||
            ''
        ).trim();
        const taxOffice = String(corp?.billing?.taxAdministration || '').trim();
        if (title) map.set(title, taxNo);
        if (taxNo) taxNoMap.set(taxNo, { accountCode, taxOffice });
      });

      corpMapRef.current = map;
      taxNoMapRef.current = taxNoMap;
      setCorpTitles(Array.from(map.keys()));
    } catch (e) {
      console.error('flow/get-corporations error:', e);
      toast.error('Kurumsal listesi alınamadı.');
      corpMapRef.current = new Map();
      setCorpTitles([]);
    } finally {
      setCorpLoading(false);
    }
  };

  const hasFetchedRef = React.useRef(false);
  React.useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    fetchCorporations();
  }, []);

  // HotTable ilk kurulum: SADECE columns/headers/afterChange.
  React.useEffect(() => {
    const hot = hotRef.current?.hotInstance;
    if (!hot) return;

    hot.updateSettings({
      columns: columnSettings,
      colHeaders: columnHeaders,
      rowHeights: 35,
      licenseKey: 'non-commercial-and-evaluation',
      stretchH: 'all',
      afterChange: (changes, source) => {
        if (!changes || source === 'loadData') return;
        const map = corpMapRef.current;
        changes.forEach(([row, prop, oldValue, newValue]) => {
          const colIndex =
            typeof prop === 'number' ? prop : hot.propToCol(prop);
          if (colIndex === 3 && newValue !== oldValue) {
            const tax = map.get(String(newValue).trim()) || '';
            hot.setDataAtCell(row, 4, tax);
          }
        });
      },
    });
  }, [columnSettings, columnHeaders]);

  // Başarıyla oluşan belirli satırları temizleyen yardımcı (React state üzerinden)
  const clearRowsByIndexes = (rowIndexes) => {
    if (!Array.isArray(rowIndexes) || !rowIndexes.length) return;
    setHotData((prev) =>
      prev.map((row, idx) =>
        rowIndexes.includes(idx) ? [row[0], row[1], '', '', ''] : row
      )
    );
  };

  // Toplu oluştur
  const handleCreate = () => {
    const hot = hotRef.current?.hotInstance;
    if (!hot) return;

    const table = hot.getData();

    const toSend = [];
    const rowIndexes = [];
    table.forEach((row, idx) => {
      const filled = row
        .slice(1, 5)
        .every((cell) => String(cell || '').trim() !== '');
      if (filled) {
        const taxNo = String(row[4] || '').trim();
        const corpData = taxNoMapRef.current.get(taxNo);
        toSend.push({
          mallID: row[1],
          title: row[2],
          legalTitle: row[3],
          taxNo,
          companyCorporationalCode: corpData?.accountCode || '',
          taxAdministration: corpData?.taxOffice || '',
        });
        rowIndexes.push(idx);
      }
    });

    if (!toSend.length) {
      openSuccessModal('Lütfen boş alan bırakmayınız');
      return;
    }

    api
      .post('flow/mass-branch-import', toSend)
      .then((response) => {
        const resArr =
          (Array.isArray(response?.data?.result) && response.data.result) ||
          (Array.isArray(response?.data?.data) && response.data.data) ||
          (Array.isArray(response?.data?.rows) && response.data.rows) ||
          null;

        if (Array.isArray(resArr) && resArr.length === toSend.length) {
          const successRowIdx = [];
          resArr.forEach((item, i) => {
            const ok =
              item === true ||
              item === 'OK' ||
              item?.ok === true ||
              item?.success === true ||
              item?.status === true;
            if (ok) successRowIdx.push(rowIndexes[i]);
          });

          if (successRowIdx.length) {
            clearRowsByIndexes(successRowIdx);
            openSuccessModal(
              `Toplam ${successRowIdx.length} adet mağaza oluştu.`
            );
          } else {
            openSuccessModal('Bir hata oluştu');
          }
        } else if (response?.data?.status === true) {
          clearRowsByIndexes(rowIndexes);
          openSuccessModal(`Toplam ${rowIndexes.length} adet mağaza oluştu.`);
        } else {
          openSuccessModal('Bir hata oluştu');
        }
      })
      .catch(() => {
        toast.error('Mağazalar aktarılırken bir hata oluştu');
      });
  };

  // Ön doğrulama
  const handlePreCreateValidation = () => {
    const hot = hotRef.current?.hotInstance;
    if (!hot) return;
    const table = hot.getData();

    const invalidRows = table.filter((row) => {
      const store = row[2];
      const corp = row[3];
      const tax = row[4];
      return (store || corp || tax) && !(store && corp && tax);
    });

    if (invalidRows.length > 0) {
      toast.error(
        'Mağaza Adı, Ticari Ünvan ve Vergi No aynı satırda eksiksiz doldurulmalıdır.'
      );
    } else {
      handleCreate();
    }
  };

  const openSuccessModal = (message) => {
    setSuccessMessage(message);
    setSuccessModalOpen(true);
  };

  // Excel içe aktarım
  const handleFileSelect = (e) => {
    const fileTypes = [
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/csv',
    ];
    const selectedFile = e.target.files[0];

    if (!selectedFile) return toast.error('Lütfen Excel dosyası seçiniz');
    if (!fileTypes.includes(selectedFile.type))
      return toast.error('Lütfen Excel dosyası seçiniz');

    const reader = new FileReader();
    reader.readAsArrayBuffer(selectedFile);
    reader.onload = (ev) => {
      const workbook = XLSX.read(ev.target.result, { type: 'buffer' });
      const worksheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[worksheetName];
      const data = XLSX.utils.sheet_to_json(worksheet);

      if (!data.length) return toast.error('Excel dosyası boş');

      const headers = columnHeaders;
      const map = corpMapRef.current;

      const columnMappings = {
        [language.commons.column.no]: 'id',
        [language.commons.column.mall_id]: 'mallID',
        [language.commons.column.store_name]: 'title',
        [language.commons.column.commerce_name]: 'legalTitle',
        [language.commons.column.tax_no]: 'taxNo',
      };

      const mappedData = data.map((row, index) => {
        const newRow = [];
        headers.forEach((header) => {
          if (header === language.commons.column.mall_id) {
            newRow.push(storedMallID);
          } else if (header === language.commons.column.no) {
            newRow.push(index + 1);
          } else {
            const key = columnMappings[header];
            newRow.push(row[key] || '');
          }
        });
        const corpTitle = String(newRow[3] || '').trim();
        if (corpTitle && !String(newRow[4] || '').trim()) {
          newRow[4] = map.get(corpTitle) || '';
        }
        return newRow;
      });

      setHotData(mappedData);
    };
  };

  return (
    <>
      <Helmet>
        <title>{language.fetch_branches.titles.site_title}</title>
      </Helmet>

      <Stack sx={{ px: '25px' }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={5}
        >
          <Typography variant="h4" gutterBottom>
            {language.fetch_branches.titles.title}
          </Typography>

          <div>
            <Button
              onClick={handlePreCreateValidation}
              variant="contained"
              endIcon={<SendIcon />}
              sx={{ mr: 1.5 }}
            >
              {language.fetch_branches.titles.stores_transfer}
            </Button>
            <Button component="label" variant="contained" sx={{ mr: 1.5 }}>
              {language.commons.excel_transfer}
              <Input
                type="file"
                accept=".xlsx, .xls, .csv"
                sx={{ display: 'none' }}
                onChange={handleFileSelect}
              />
            </Button>
          </div>
        </Stack>

        <Card sx={{ position: 'relative' }}>
          {corpLoading && (
            <>
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  bgcolor: 'rgba(255,255,255,0.5)',
                  zIndex: 1,
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  zIndex: 2,
                }}
              >
                <LinearProgress />
              </Box>
            </>
          )}
          <Scrollbar>
            <Box sx={{ width: '100%' }}>
              <HotTable
                ref={hotRef}
                data={hotData} // 🔑 Veriyi prop'tan veriyoruz
                rowHeights={35}
                licenseKey="non-commercial-and-evaluation"
                // persistentState={true}  // İstersen yeniden aç; data yönetimini React state yapıyor.
              />
            </Box>
          </Scrollbar>
        </Card>

        <Modal
          open={isSuccessModalOpen}
          onClose={() => setSuccessModalOpen(false)}
        >
          <Box
            sx={{
              position: 'absolute',
              width: 400,
              bgcolor: 'background.paper',
              border: '2px solid #000',
              boxShadow: 24,
              p: 4,
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              textAlign: 'center',
            }}
          >
            {successMessage.includes('Lütfen boş alan bırakmayınız') ||
            successMessage.includes('Bir hata oluştu') ? (
              <WarningOutlined sx={{ color: 'orange', fontSize: 64 }} />
            ) : (
              <CheckCircleIcon sx={{ color: 'green', fontSize: 64 }} />
            )}
            <Typography variant="h6">{successMessage}</Typography>
            <Divider sx={{ my: 2, backgroundColor: 'gray' }} />
            <Button onClick={() => setSuccessModalOpen(false)}>
              {language.commons.okay}
            </Button>
          </Box>
        </Modal>
      </Stack>
    </>
  );
}
